import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import Locale from "../core/locale/locale";

/* ------------------------------------------------------------------ */
/* Dayjs plugins (extend ONCE globally)                                */
/* ------------------------------------------------------------------ */
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export type DateInput =
    | string
    | Date
    | Dayjs
    | null;

/**
 * Safely converts unknown input into Dayjs (or null)
 * - Handles Date / Dayjs
 * - Rejects invalid dates
 * - Applies app timezone consistently
 */
const toDayjs = (value: DateInput): Dayjs | null => {
    if (!value) return null;

    if (dayjs.isDayjs(value)) {
        return value.isValid() ? value : null;
    }

    const parsed = dayjs.tz(
        value,
        Locale.timezone
    );

    return parsed.isValid() ? parsed : null;
};

/**
 * Fallback value for UI when date is invalid or missing
 */
const UI_EMPTY = "-";

/* ------------------------------------------------------------------ */
/* UI formatters                                                       */
/* ------------------------------------------------------------------ */

// export const formatDate = (value: DateInput): string => {
//     const date = toDayjs(value);
//     if (!date) return UI_EMPTY;
//
//     return date.format(Locale.displayDate);
// };
//
// export const formatTime = (value: DateInput): string => {
//     const date = toDayjs(value);
//     if (!date) return UI_EMPTY;
//
//     return date.format(Locale.displayTime);
// };
//
// export const formatDateTime = (value: DateInput): string => {
//     const date = toDayjs(value);
//     if (!date) return UI_EMPTY;
//
//     return date.format(Locale.displayDateTime);
// };

/* ------------------------------------------------------------------ */
/* API converters                                                      */
/* ------------------------------------------------------------------ */

/* Converts UI Dayjs input into → backend-safe datetime string */
export const convertToApiFormat = (value?: Dayjs | null): Date | undefined => {
    if (dayjs.isDayjs(value) && value.isValid()) {
        return value.utc(true).toDate();
    } else {
        return undefined;
    }
};

/* Converts API datetime format to → UI friendly Dayjs format */
export const convertToDisplayFormat = (value?: Date): Dayjs | null => {
    if (value) {
        const parsed = dayjs(value, Locale.dateFormat);
        return parsed.isValid() ? parsed : null;
    } else {
        return null;
    }
};

/* ------------------------------------------------------------------ */
/* Comparators (business-safe)                                         */
/* ------------------------------------------------------------------ */

export const isAfter = (
    a: DateInput,
    b: DateInput
): boolean => {
    const dateA = toDayjs(a);
    const dateB = toDayjs(b);

    if (!dateA || !dateB) return false;
    return dateA.isAfter(dateB);
};

export const isBefore = (
    a: DateInput,
    b: DateInput
): boolean => {
    const dateA = toDayjs(a);
    const dateB = toDayjs(b);

    if (!dateA || !dateB) return false;
    return dateA.isBefore(dateB);
};

/* ------------------------------------------------------------------ */
/* Guards & utilities                                                  */
/* ------------------------------------------------------------------ */

export const isValidDate = (value: DateInput): boolean => {
    return Boolean(toDayjs(value));
};

/**
 * Normalizes date to start of day (useful for policies, leaves, rules)
 */
export const startOfDay = (value: DateInput): Dayjs | null => {
    const date = toDayjs(value);
    return date ? date.startOf("day") : null;
};

/**
 * Normalizes date to end of day
 */
export const endOfDay = (value: DateInput): Dayjs | null => {
    const date = toDayjs(value);
    return date ? date.endOf("day") : null;
};
